import { useState, useRef, useEffect } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input, { type InputRef } from "../../components/core/input.core";
import Button from "../../components/core/button.core";
import { useEmailVerifyOtpBrandHttp } from "../../hooks/http/auth/email-verify-otp.hook";
import type { IOtp } from "../../../types/models/otp.models.types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserStore } from "../../../store/user.store";
import { useDashboardConfigCommonHttp } from "../../../hooks/http/common/users/dashboard-config.common.http";
import type { IUser } from "../../../types/models/user.models.types";
import type { IBrands } from "../../hooks/http/colab/workspace/get-workspace.hook";
import { AccountType } from "../../../constants/account-types.constants";
import { handleHttpError } from "../../../utils/utils";
import { useToast } from "../../../hooks/use-toast";

// OTP form schema
const OtpSchema = z.object({
  otp: z.string().min(6, { message: "Please enter a valid OTP" }),
});

// Infer the type from the schema
type OtpFormValues = z.infer<typeof OtpSchema>;

interface OtpVerificationFormProps {
  canResend: boolean;
  resendIn: number;
  loading?: boolean;
  onResend: () => void;
  authenticationState: IOtp;
}

export default function OtpVerificationForm({
  canResend,
  resendIn,
  loading,
  onResend,
  authenticationState,
}: OtpVerificationFormProps) {
  const [formErrors] = useState<string[]>([]);
  const [time, setTime] = useState<number>(resendIn);
  const otpRef = useRef<InputRef>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useUserStore();
  const [searchParams] = useSearchParams();
  const proposalId = searchParams.get("proposalId");

  const { execute: verifyOtpHttp, fetching: verifyingOtp } =
    useEmailVerifyOtpBrandHttp();

  const { execute: fetchConfig, fetching: fetchingConfig } =
    useDashboardConfigCommonHttp();

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(OtpSchema),
    defaultValues: {
      otp: "",
    },
  });

  async function getConfig() {
    const response = await fetchConfig({ body: null });
    if (!response.success) {
      handleHttpError(response, toast);
      return;
    }

    return response.data;
  }

  const onSubmitOtp = otpForm.handleSubmit(async (data) => {
    console.log("OTP submitted successfully:", data);
    if (!authenticationState.id) return;
    // Add your form submission logic here
    const response = await verifyOtpHttp({
      body: {
        id: authenticationState.id,
        otp: data.otp,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }

    login(
      response.data.token,
      {
        id: "",
        name: "",
        email: "",
        accountType: AccountType.BRAND,
        phoneNumber: "",
        identifier: "",
      },
      [],
    );
    const config = await getConfig();
    login(
      response.data.token,
      config?.user as IUser,
      config?.brands as IBrands[],
    );

    if (config?.brands === null) {
      navigate("/brands/workspace/join");
      return;
    }

    if (config?.user.name === null || config?.user.name === "") {
      navigate("/brands/auth/complete-profile");
      return;
    }

    if (proposalId) {
      return navigate(`/brands/proposals/${proposalId}`);
    }
    return navigate("/");
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (canResend && time > 0) {
      const interval = setInterval(() => {
        setTime((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [canResend, time, resendIn]);

  // Reset timer when onResend is called
  const handleResend = () => {
    onResend();
    setTime(resendIn);
  };

  function ReturnTimer() {
    if (canResend) {
      return time === 0 ? (
        // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
        <p onClick={handleResend} className="cursor-pointer">
          Resend code
        </p>
      ) : (
        <p className="cursor-not-allowed">Resend code {time}</p>
      );
    }
    return "Resend limit exceeded";
  }

  return (
    <div className="flex flex-col items-center gap-10 bg-white px-5 pt-10 w-full h-screen sm:h-fit text-soloprimary">
      <form
        onSubmit={onSubmitOtp}
        className="flex flex-col justify-between w-full max-w-md h-full"
      >
        <div className="flex flex-col w-full">
          <h1 className="pb-10 font-cabinetgrotesk font-medium text-3xl tracking-tight">
            Verify your email
          </h1>

          {formErrors.length > 0 && (
            <div className="bg-red-100 mb-4 px-4 py-3 border border-red-400 rounded text-red-700">
              {formErrors.map((error, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                <p key={index}>{error}</p>
              ))}
            </div>
          )}

          <Controller
            control={otpForm.control}
            name="otp"
            render={({ field, fieldState }) => (
              <Input
                ref={otpRef}
                label="Enter the 6 digit OTP sent to your email"
                placeholder="XXXXXX"
                value={field.value}
                onChange={(e) => {
                  console.log("OTP input change:", e.target.value);
                  // Ensure only numbers are entered and max length is 6
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                className="py-4 tracking-widest"
                type="text"
                inputMode="numeric"
              />
            )}
          />
          <div className="flex justify-between items-center pt-2 w-full">
            <p className="font-cabinetgrotesk text-secondary text-sm">
              Didn't receive OTP?
            </p>
            <p className="font-cabinetgrotesk font-medium text-black text-sm">
              {loading ? "Sending otp..." : ReturnTimer()}
            </p>
          </div>
        </div>

        <div className="justify-center items-center pt-8 pb-4 w-full">
          <Button
            className="w-full font-semibold font-sm"
            label="Continue"
            type="button"
            isLoading={verifyingOtp || fetchingConfig}
            onClick={onSubmitOtp}
          />
        </div>
      </form>
    </div>
  );
}
